import { useState, useEffect, useMemo } from 'react'
import { Modal, Table, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'
import AccountSearchModal from '../Accounting/AccountSearchModal'

/**
 * Modal para revisar y corregir cuentas del asiento propuesto antes de aplicar.
 * Montos fijos; solo se edita account_id por línea (key).
 */
const RevisarAsientoContableModal = ({
  show,
  onHide,
  title = 'Revisar asiento contable',
  propuesta,
  isLoading = false,
  error = null,
  clientId,
  onConfirm,
  confirmLabel = 'Aplicar asiento',
  isConfirming = false,
}) => {
  const [lineasLocal, setLineasLocal] = useState([])
  const [cuentaModal, setCuentaModal] = useState({ show: false, key: null })

  useEffect(() => {
    if (show && propuesta?.lineas) {
      setLineasLocal(propuesta.lineas.map((l) => ({ ...l })))
    }
  }, [show, propuesta])

  const { data: cuentasHint } = useQuery(
    ['accounts-hint-asiento', clientId],
    async () => {
      if (!clientId) return []
      const res = await api.get('/accounting/accounts/', {
        params: { cliente_id: clientId, is_active: true, page_size: 5 },
      })
      return res.data.results || res.data || []
    },
    { enabled: !!show && !!clientId, staleTime: 60000 }
  )

  const totals = useMemo(() => {
    let d = 0
    let h = 0
    lineasLocal.forEach((l) => {
      d += parseFloat(l.debit || 0)
      h += parseFloat(l.credit || 0)
    })
    return { debe: d, haber: h, ok: Math.abs(d - h) < 0.015 }
  }, [lineasLocal])

  const faltantes = lineasLocal.filter(
    (l) =>
      (l.requerida !== false) &&
      !l.account_id &&
      (parseFloat(l.debit || 0) > 0 || parseFloat(l.credit || 0) > 0)
  )

  const handleSelectCuenta = (cuenta) => {
    if (!cuentaModal.key || !cuenta?.id) return
    setLineasLocal((prev) =>
      prev.map((l) =>
        l.key === cuentaModal.key
          ? {
              ...l,
              account_id: cuenta.id,
              account_code: cuenta.code || '',
              account_name: cuenta.name || '',
            }
          : l
      )
    )
    setCuentaModal({ show: false, key: null })
  }

  const handleConfirm = () => {
    const overrides = lineasLocal
      .filter((l) => l.account_id)
      .map((l) => ({ key: l.key, account_id: l.account_id }))
    onConfirm?.({ lineas_contables: overrides, lineas: lineasLocal })
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Cargando propuesta…
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <>
              {(propuesta?.warnings || []).length > 0 && (
                <Alert variant="warning" className="py-2">
                  <ul className="mb-0 small">
                    {propuesta.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              <p className="text-muted small mb-2">
                Revise las cuentas elegidas automáticamente. Solo puede cambiar la cuenta de cada
                línea; los importes se mantienen. IVA va en línea separada cuando aplica.
              </p>
              <Table size="sm" bordered responsive hover>
                <thead>
                  <tr>
                    <th>Rol</th>
                    <th>Descripción</th>
                    <th>Cuenta</th>
                    <th className="text-end">Debe</th>
                    <th className="text-end">Haber</th>
                  </tr>
                </thead>
                <tbody>
                  {lineasLocal.map((l) => (
                    <tr key={l.key}>
                      <td>
                        <Badge bg="secondary">{l.rol}</Badge>
                      </td>
                      <td>
                        <small>{l.description}</small>
                      </td>
                      <td style={{ minWidth: 220 }}>
                        <div className="d-flex gap-1 align-items-center">
                          <Form.Control
                            size="sm"
                            readOnly
                            value={
                              l.account_code
                                ? `${l.account_code} — ${l.account_name || ''}`
                                : 'Sin cuenta'
                            }
                            className={!l.account_id ? 'border-danger' : ''}
                          />
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setCuentaModal({ show: true, key: l.key })}
                            title="Cambiar cuenta"
                          >
                            <i className="bi bi-pencil" />
                          </Button>
                        </div>
                      </td>
                      <td className="text-end">{formatCurrency(l.debit || 0)}</td>
                      <td className="text-end">{formatCurrency(l.credit || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={3} className="text-end">
                      Totales{' '}
                      {totals.ok ? (
                        <Badge bg="success">Cuadra</Badge>
                      ) : (
                        <Badge bg="danger">No cuadra</Badge>
                      )}
                    </th>
                    <th className="text-end">{formatCurrency(totals.debe)}</th>
                    <th className="text-end">{formatCurrency(totals.haber)}</th>
                  </tr>
                </tfoot>
              </Table>
              {faltantes.length > 0 && (
                <Alert variant="danger" className="py-2 small">
                  Faltan cuentas en {faltantes.length} línea(s). Asígneas antes de aplicar.
                </Alert>
              )}
              {cuentasHint?.length === 0 && clientId && (
                <Form.Text className="text-muted">
                  No hay cuentas en el catálogo del cliente; verifique el plan de cuentas.
                </Form.Text>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isConfirming}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={
              isLoading ||
              !!error ||
              isConfirming ||
              !totals.ok ||
              faltantes.length > 0 ||
              lineasLocal.length === 0
            }
            onClick={handleConfirm}
          >
            {isConfirming ? (
              <>
                <Spinner size="sm" className="me-1" /> Aplicando…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AccountSearchModal
        show={cuentaModal.show}
        onHide={() => setCuentaModal({ show: false, key: null })}
        onSelect={handleSelectCuenta}
        clienteId={clientId || ''}
        pageSize={200}
      />
    </>
  )
}

export default RevisarAsientoContableModal
